'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { getTerraGraphCategoryColors } from '@terra/ui-styled-system';
import { useThemeMode } from '@terra/ui-design-system';
import type { DonutTooltipParams, ChartLegendFormatterOpts } from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface DonutChartProps {
  series: number[];
  labels: string[];
  title?: string;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  showDataLabels?: boolean;
  tooltipCustom?: (options: DonutTooltipParams) => string;
  tooltipFormatter?: (value: number) => string;
  showLegend?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  onDataPointClick?: (
    dataPointIndex: number,
    label: string,
    value: number,
  ) => void;
  legendLabelFormatter?: (
    label: string,
    opts: ChartLegendFormatterOpts,
  ) => string;
  legendItemMargin?: {
    horizontal?: number;
    vertical?: number;
  };
  centerLabel?: string;
}

export function DonutChart({
  series,
  labels,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  showDataLabels = true,
  tooltipCustom,
  tooltipFormatter,
  showLegend = true,
  legendPosition = 'bottom',
  onDataPointClick,
  legendLabelFormatter,
  legendItemMargin,
  centerLabel,
}: DonutChartProps) {
  const { mode: themeMode } = useThemeMode();

  const options = useMemo<ApexOptions | null>(() => {
    if (!series || !labels || series.length !== labels.length) {
      return null;
    }

    return {
      chart: {
        type: 'donut',
        toolbar: {
          show: false,
        },
        foreColor: 'currentColor',
        animations: {
          enabled: true,
          speed: 300,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
        events: {
          dataPointSelection: onDataPointClick
            ? (event, chartContext, opts) => {
                const { dataPointIndex } = opts;
                const label = labels[dataPointIndex];
                const value = series[dataPointIndex];

                onDataPointClick(dataPointIndex, label, value);
              }
            : undefined,
        },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          ...(centerLabel
            ? {
                donut: {
                  size: '72%',
                  labels: {
                    show: true,
                    name: { show: false },
                    value: { show: false },
                    total: {
                      show: true,
                      showAlways: true,
                      label: '',
                      fontSize: '22px',
                      fontWeight: 700,
                      formatter: () => centerLabel,
                    },
                  },
                },
              }
            : {}),
        },
      },
      labels,
      colors,
      dataLabels: {
        enabled: showDataLabels,
        formatter: (val: number) => {
          return [val.toFixed(1) + '%'];
        },
        style: {
          fontSize: '14px',
          fontFamily: 'inherit',
          fontWeight: 'normal',
        },
        dropShadow: {
          enabled: true,
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          fontSize: '16px',
          fontWeight: 'normal',
        },
      },
      tooltip: {
        enabled: true,
        followCursor: false,
        ...(tooltipCustom
          ? { custom: tooltipCustom }
          : { theme: themeMode === 'dark' ? 'dark' : 'light' }),
        y: {
          ...(tooltipFormatter && { formatter: tooltipFormatter }),
        },
      },
      legend: {
        show: showLegend,
        position: legendPosition,
        fontSize: '14px',
        fontFamily: 'inherit',
        labels: {
          useSeriesColors: false,
        },
        ...(legendLabelFormatter && { formatter: legendLabelFormatter }),
        markers: {
          size: 4,
          strokeWidth: 0,
          offsetX: -4,
        },
        ...(legendItemMargin && { itemMargin: legendItemMargin }),
      },
      stroke: {
        width: 0,
      },
      states: {
        hover: {
          filter: {
            type: 'lighten',
            value: 0.25,
          },
        },
      },
    };
  }, [
    series,
    labels,
    colors,
    showDataLabels,
    title,
    tooltipCustom,
    tooltipFormatter,
    showLegend,
    legendPosition,
    themeMode,
    onDataPointClick,
    legendLabelFormatter,
    legendItemMargin,
    centerLabel,
  ]);

  const chartKey = useMemo(() => {
    return JSON.stringify({ series, labels });
  }, [series, labels]);

  if (!options) {
    return null;
  }

  return (
    <ReactApexChart
      // force re-render when series or labels change
      // needs to be done because apexcharts has internal state that is not updated when passing previously rendered options
      key={chartKey}
      options={options}
      series={series}
      type="donut"
      height={height}
      width={width}
    />
  );
}

export default DonutChart;
